import {
  CloseOutlined,
  SaveOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState } from "react";
import PasswordForm from "../../components/account/PasswordForm";
import PermissionTree from "../../components/account/PermissionTree";
import { IUserFilter } from "../../models/UserFilter";
import { ICreateUser, IUserSystem } from "../../models/Users";
import {
  useGetDepartmentById,
  useGetDepartments,
} from "../../query/DepartmentQuery";
import {
  useCreateUser,
  useUpdateUser,
  useUsersWithFilters,
} from "../../query/UserQuery";
import { notify } from "../../utils/toast";

const AccountSystem: React.FC = () => {
  const [form] = Form.useForm();
  const [editingUser, setEditingUser] = useState<IUserSystem | null>(null);
  const [filters, setFilters] = useState<IUserFilter>({
    page: 1,
    limit: 10,
  });
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>();

  const { data: users, isLoading } = useUsersWithFilters(filters);
  const { data: departments, isLoading: isLoadingDepartments } =
    useGetDepartments();
  const { data: departmentDetail, isLoading: isLoadingPositions } =
    useGetDepartmentById(selectedDepartmentId || 0);
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const columns: ColumnsType<IUserSystem> = [
    {
      title: "Họ và tên",
      dataIndex: "fullName",
      key: "fullName",
      align: "center",
      width: 150,
      fixed: "left",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "center",
      width: 200,
    },
    {
      title: "Phòng ban",
      dataIndex: ["department", "departmentCode"],
      key: "department",
      align: "center",
      width: 120,
    },
    {
      title: "Vị trí",
      dataIndex: ["position", "positionTitle"],
      key: "position",
      align: "center",
      width: 150,
    },
    {
      title: "Vai trò",
      dataIndex: "role",
      key: "role",
      render: (role: string) => {
        let color =
          role === "SUPER_ADMIN"
            ? "red"
            : role === "MANAGER"
              ? "blue"
              : "green";
        return (
          <Tag color={color} style={{ textTransform: "uppercase" }}>
            {role}
          </Tag>
        );
      },
      align: "center",
      width: 120,
    },
    {
      title: "Trạng thái",
      dataIndex: "isActive",
      key: "status",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "success" : "error"}>
          {isActive ? "Hoạt động" : "Vô hiệu"}
        </Tag>
      ),
      align: "center",
      width: 120,
    },
  ];
  const handleSave = async (values: ICreateUser) => {
    try {
      if (editingUser) {
        await updateUserMutation.mutateAsync({
          id: editingUser.id,
          data: {
            fullName: values.fullName,
            departmentId: values.departmentId,
            positionId: values.positionId,
            role: values.role,
            isActive: values.isActive,
          },
        });
        notify.success("Cập nhật tài khoản thành công");
        handleCancel();
      } else {
        await createUserMutation.mutateAsync(values);
        notify.success("Tạo tài khoản thành công");
        handleCancel();
      }
    } catch (error) {}
  };

  const handleEdit = (user: IUserSystem) => {
    setEditingUser(user);
    form.setFieldsValue({
      fullName: user.fullName,
      email: user.email,
      departmentId: user.departmentId,
      positionId: user.positionId,
      role: user.role,
      isActive: user.isActive,
    });
  };

  const handleCancel = () => {
    setEditingUser(null);
    form.resetFields();
  };

  const handleAddNew = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      role: "USER",
    });
  };

  return (
    <div
      className="p-2 sm:p-4"
      style={{ backgroundColor: "var(--bg-primary)" }}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title="Danh sách tài khoản"
            extra={
              !editingUser && (
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={handleAddNew}
                >
                  <span className="hidden sm:inline">Thêm tài khoản</span>
                  <span className="sm:hidden">Thêm</span>
                </Button>
              )
            }
            style={{ marginBottom: "16px" }}
          >
            <Table
              dataSource={users?.data}
              rowKey="id"
              columns={columns}
              scroll={{ x: 800, y: 650 }}
              pagination={{
                current: filters.page,
                pageSize: filters.limit,
                total: users?.pagination.total,
                onChange: (page, pageSize) => {
                  setFilters({
                    ...filters,
                    page,
                    limit: pageSize,
                  });
                },
              }}
              loading={isLoading}
              onRow={(record) => ({
                onClick: () => {
                  handleEdit(record);
                  setSelectedDepartmentId(record.departmentId);
                },
              })}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card style={{ backgroundColor: "var(--bg-primary)" }}>
            <Tabs
              defaultActiveKey="info"
              items={[
                {
                  key: "info",
                  label: editingUser
                    ? "Thông tin tài khoản"
                    : "Thêm tài khoản mới",
                  children: (
                    <>
                      <div style={{ marginBottom: 16 }}>
                        <Space>
                          <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={async () => {
                              try {
                                const values = await form.validateFields();
                                await handleSave(values);
                                form.resetFields();
                              } catch (error) {
                                notify.error("Vui lòng kiểm tra lại thông tin");
                              }
                            }}
                            loading={
                              createUserMutation.isPending ||
                              updateUserMutation.isPending
                            }
                          >
                            {editingUser ? "Cập nhật" : "Tạo mới"}
                          </Button>
                          <Button
                            icon={<CloseOutlined />}
                            onClick={handleCancel}
                          >
                            Hủy
                          </Button>
                        </Space>
                      </div>

                      <Form
                        form={form}
                        layout="vertical"
                        initialValues={{ status: "active" }}
                        style={{ backgroundColor: "var(--bg-primary)" }}
                      >
                        <Form.Item
                          name="fullName"
                          label={
                            <span className="text-gray-700 dark:text-gray-300 font-medium">
                              Họ và tên
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Vui lòng nhập họ và tên",
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item
                          name="email"
                          label={
                            <span className="text-gray-700 dark:text-gray-300 font-medium">
                              Email
                            </span>
                          }
                          rules={[
                            { required: true, message: "Vui lòng nhập email" },
                            { type: "email", message: "Email không hợp lệ" },
                          ]}
                        >
                          <Input disabled={!!editingUser} />
                        </Form.Item>

                        {!editingUser && (
                          <Form.Item
                            name="password"
                            label={
                              <span className="text-gray-700 dark:text-gray-300 font-medium">
                                Mật khẩu
                              </span>
                            }
                            rules={[
                              {
                                required: true,
                                message: "Vui lòng nhập mật khẩu",
                              },
                            ]}
                          >
                            <Input.Password />
                          </Form.Item>
                        )}

                        <Form.Item
                          name="departmentId"
                          label={
                            <span className="text-gray-700 dark:text-gray-300 font-medium">
                              Phòng ban
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Vui lòng chọn phòng ban",
                            },
                          ]}
                        >
                          <Select
                            loading={isLoadingDepartments}
                            onChange={(value) => setSelectedDepartmentId(value)}
                          >
                            {departments?.map((dept) => (
                              <Select.Option key={dept.id} value={dept.id}>
                                {dept.description}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="positionId"
                          label={
                            <span className="text-gray-700 dark:text-gray-300 font-medium">
                              Vị trí
                            </span>
                          }
                          rules={[
                            { required: true, message: "Vui lòng chọn vị trí" },
                          ]}
                        >
                          <Select
                            loading={isLoadingPositions}
                            disabled={!selectedDepartmentId}
                          >
                            {departmentDetail?.positions?.map((pos) => (
                              <Select.Option key={pos.id} value={pos.id}>
                                {pos.positionTitle}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          name="role"
                          label={
                            <span className="text-gray-700 dark:text-gray-300 font-medium">
                              Vai trò
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Vui lòng chọn vai trò",
                            },
                          ]}
                        >
                          <Select
                            disabled={editingUser?.role === "SUPER_ADMIN"}
                          >
                            <Select.Option value="ADMIN">
                              Quản trị viên
                            </Select.Option>
                            <Select.Option value="MANAGER">
                              Quản lý
                            </Select.Option>
                            <Select.Option value="USER">
                              Người dùng
                            </Select.Option>
                            <Select.Option value="STAFF">
                              Nhân viên
                            </Select.Option>
                            <Select.Option value="GUEST">Khách</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="isActive"
                          label={
                            <span className="text-gray-700 dark:text-gray-300 font-medium">
                              Trạng thái
                            </span>
                          }
                          valuePropName="checked"
                        >
                          <Switch
                            disabled={editingUser?.role === "SUPER_ADMIN"}
                            style={{ width: 120 }}
                            checkedChildren="Hoạt động"
                            unCheckedChildren="Vô hiệu"
                            defaultChecked
                          />
                        </Form.Item>
                      </Form>
                    </>
                  ),
                },

                ...(editingUser
                  ? [
                      {
                        key: "permissions",
                        label: "Phân quyền",

                        children: <PermissionTree selectedUser={editingUser} />,
                      },
                    ]
                  : []),
                ...(editingUser
                  ? [
                      {
                        key: "password",
                        label: "Đổi mật khẩu",
                        children: <PasswordForm selectedUser={editingUser} />,
                      },
                    ]
                  : []),
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AccountSystem;
